import { STORAGE_KEY } from './sessionStorage';
import type { AuthSession } from './types';

const ATTEMPT_ID_KEY = `${STORAGE_KEY}:post-auth-attempt-id`;
const ATTEMPT_USER_ID_KEY = `${STORAGE_KEY}:post-auth-attempt-user-id`;
const ATTEMPT_CONSUMED_KEY = `${STORAGE_KEY}:post-auth-attempt-consumed`;

const safeRead = (key: string): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeWrite = (key: string, value: string): void => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // ignore storage failures
  }
};

const safeRemove = (key: string): void => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // ignore storage failures
  }
};

export const clearPostAuthRedirectAttempt = (): void => {
  safeRemove(ATTEMPT_ID_KEY);
  safeRemove(ATTEMPT_USER_ID_KEY);
  safeRemove(ATTEMPT_CONSUMED_KEY);
};

export const setPostAuthRedirectAttempt = (input: { attemptId: string; userId: string }): void => {
  safeWrite(ATTEMPT_ID_KEY, input.attemptId);
  safeWrite(ATTEMPT_USER_ID_KEY, input.userId);
  safeWrite(ATTEMPT_CONSUMED_KEY, '0');
};

export const ensurePostAuthRedirectAttempt = (session: AuthSession): string => {
  const storedAttemptId = safeRead(ATTEMPT_ID_KEY);
  const storedUserId = safeRead(ATTEMPT_USER_ID_KEY);
  if (storedAttemptId && storedUserId === session.userId) {
    return storedAttemptId;
  }
  const fallbackAttemptId = `session:${session.userId}:${session.expiresAt ?? Date.now()}`;
  setPostAuthRedirectAttempt({ attemptId: fallbackAttemptId, userId: session.userId });
  return fallbackAttemptId;
};

export const consumePostAuthRedirectAttempt = (input: {
  attemptId: string;
  userId: string;
}): boolean => {
  const storedAttemptId = safeRead(ATTEMPT_ID_KEY);
  const storedUserId = safeRead(ATTEMPT_USER_ID_KEY);
  const consumed = safeRead(ATTEMPT_CONSUMED_KEY) === '1';
  if (storedAttemptId !== input.attemptId || storedUserId !== input.userId || consumed) {
    return false;
  }
  safeWrite(ATTEMPT_CONSUMED_KEY, '1');
  return true;
};
